import { useEffect, useState } from 'react';
import { InputText } from 'primereact';
import { RadioButton } from 'primereact/radiobutton';
import { AutoComplete } from 'primereact/autocomplete';
import { HatGlasses, PictureInPicture } from 'lucide-react';
import { Panel } from 'primereact/panel';
import { InputSwitch } from 'primereact/inputswitch';

import { getDashboardList } from '../service/widget.service';

import CreativeColorPicker from './CreativeColorPicker';

const ButtonWidget = ({ widgetFormik, action, setRoutingWidgetHandler }) => {
  const [dashboardList, setDashboardList] = useState([]);
  const [searchQuery, setSearchQuery] = useState({ limit: 10, offset: 0 });

  useEffect(() => {
    getDashboardList(searchQuery, setDashboardList);
  }, [searchQuery]);

  const suggestDashboard = event => {
    setSearchQuery(prevState => ({
      ...prevState,
      offset: 0,
      search_query: event?.query,
    }));
  };
  const headerTemplate = () => {
    return (
      <div className="flex align-items-center gap-2 text-sm font-medium text-gray-700">
        <HatGlasses className="text-primary text-sm w-1rem h-1rem" />
        <span className="text-sm font-bold text-gray-500">Variant & Style</span>
      </div>
    );
  };

  return (
    <Panel header={headerTemplate} className="w-full">
      <div className="flex flex-wrap gap-3">
        <span className="text-gray-700 text-sm">Button/Card Type: </span>
        <div className="flex align-items-center">
          <RadioButton
            inputId="tabularWidget"
            name="tabularWidget"
            value="tabularWidget"
            disabled={action === 'view'}
            onChange={e => {
              widgetFormik.setFieldValue('custom_properties', {
                ...widgetFormik.values.custom_properties,
                buttonWidgetType: e.value,
              });
              setRoutingWidgetHandler(parseInt(0));
              widgetFormik.setFieldValue('route_widget', parseInt(0));
            }}
            checked={
              widgetFormik?.values?.custom_properties?.buttonWidgetType ===
              'tabularWidget'
            }
          />
          <label htmlFor="tabularWidget" className="ml-2 text-sm">
            Tabular
          </label>
        </div>
        {widgetFormik.values.widget_type !== 'card' && (
          <>
            <div className="flex align-items-center">
              <RadioButton
                inputId="urlWidget"
                disabled={action === 'view'}
                name="urlWidget"
                value="urlWidget"
                onChange={e => {
                  widgetFormik.setFieldValue('custom_properties', {
                    ...widgetFormik.values.custom_properties,
                    buttonWidgetType: e.value,
                  });
                }}
                checked={
                  widgetFormik?.values?.custom_properties?.buttonWidgetType ===
                  'urlWidget'
                }
              />
              <label htmlFor="urlWidget" className="ml-2 text-sm">
                URL
              </label>
            </div>
            <div className="flex align-items-center">
              <RadioButton
                inputId="dashboardWidget"
                name="dashboardWidget"
                value="dashboardWidget"
                disabled={action == 'view'}
                onChange={e => {
                  widgetFormik.setFieldValue('custom_properties', {
                    ...widgetFormik.values.custom_properties,
                    buttonWidgetType: e.value,
                  });
                }}
                checked={
                  widgetFormik?.values?.custom_properties?.buttonWidgetType ===
                  'dashboardWidget'
                }
              />
              <label htmlFor="dashboardWidget" className="ml-2 text-sm">
                Dashboard
              </label>
            </div>
            <div className="flex align-items-center">
              <RadioButton
                inputId="readOnlyWidget"
                name="readOnlyWidget"
                value="readOnlyWidget"
                disabled={action == 'view'}
                onChange={e => {
                  widgetFormik.setFieldValue('custom_properties', {
                    ...widgetFormik.values.custom_properties,
                    buttonWidgetType: e.value,
                  });
                }}
                checked={
                  widgetFormik?.values?.custom_properties?.buttonWidgetType ===
                  'readOnlyWidget'
                }
              />
              <label htmlFor="ingredient4" className="ml-2 text-sm">
                Title
              </label>
            </div>
          </>
        )}
      </div>
      <div className="grid grid-nogutter gap-2 mt-3">
        <div className="col-4 p-0 m-0">
          <CreativeColorPicker
            widgetFormik={widgetFormik}
            action={action}
            colorPickerId="buttonColor"
            colorPickerName={'Background Color'}
          />
        </div>

        <div className="col-4 p-0 m-0">
          <CreativeColorPicker
            widgetFormik={widgetFormik}
            action={action}
            colorPickerId="buttonTextColor"
            colorPickerName={'Text Color'}
          />
        </div>
        {widgetFormik?.values?.custom_properties?.buttonWidgetType !==
          'readOnlyWidget' && (
          <div className="flex align-items-center gap-2">
            <PictureInPicture className="text-primary text-sm w-1rem h-1rem" />
            <span className="text-sm">Show Content in New Window</span>
            <InputSwitch
              checked={
                widgetFormik?.values?.custom_properties?.openIn?.code ===
                'new-tab'
                  ? true
                  : false
              }
              disabled={action == 'view'}
              onChange={event => {
                const openInValue = event.value ? 'new-tab' : 'same-tab';

                widgetFormik.setFieldValue('custom_properties', {
                  ...widgetFormik.values.custom_properties,
                  openIn: {
                    code: openInValue,
                    label: openInValue === 'new-tab' ? 'New Tab' : 'Same Tab',
                  },
                });
              }}
            />
          </div>
        )}
      </div>
      <div className="grid grid-nogutter mt-3">
        {widgetFormik?.values?.custom_properties?.buttonWidgetType ===
          'urlWidget' && (
          <div className="col-4">
            <span className="p-float-label w-full">
              <InputText
                id="url"
                value={widgetFormik.values.custom_properties.buttonInfoUrl}
                disabled={action === 'view' ? true : false}
                onChange={e => {
                  widgetFormik.setFieldValue('custom_properties', {
                    ...widgetFormik.values.custom_properties,
                    buttonInfoUrl: e.target.value,
                  });
                }}
                className="w-full p-inputtext-sm"
              />
              <label htmlFor="url">
                URL<i className="text-red-400">*</i>
              </label>
            </span>
          </div>
        )}
        {widgetFormik?.values?.custom_properties?.buttonWidgetType ===
          'dashboardWidget' && (
          <div className="col-4">
            <span className="p-float-label w-full">
              <AutoComplete
                showClear
                id="dashboard_id"
                disabled={action == 'view'}
                className="w-12"
                inputClassName="p-inputtext-sm w-12"
                suggestions={dashboardList}
                field="name"
                completeMethod={suggestDashboard}
                value={
                  widgetFormik?.values?.custom_properties?.attachedDashboard ||
                  ''
                }
                onChange={e =>
                  widgetFormik.setFieldValue('custom_properties', {
                    ...widgetFormik.values.custom_properties,
                    attachedDashboard: e.target.value,
                  })
                }
                delay={300}
                dropdown
                placeholder="Search Dashboard"
              />
              <label htmlFor="manager_id">Dashboard</label>
            </span>
          </div>
        )}
      </div>
    </Panel>
  );
};

export default ButtonWidget;
